import type { TierLike } from "@/lib/storefront/products/tiers";
// Shared product types for ecommerce widgets.
// Single shape flows: ProductCollection → ProductCard → QuickViewDialog
// so we never re-fetch product data.

import type { ProductUnitDTO, UnitDTO } from "@/lib/units/types";

export type ProductImage = {
  url: string;
  display_order: number;
  /** LQIP base64 data URL — passed to next/image's placeholder="blur". */
  blur_data_url?: string | null;
};

export type ProductVariant = {
  id: string;
  options: Record<string, string>;
  price: number;
  stock: number | null;
  image_url: string | null;
  is_active: boolean;
  /** Own minimum order quantity; null = inherit the product's. */
  min_order_qty: number | null;
  /** Set only for the quick view; cards read the product-level sale. */
  sale?: { sale_price: number; original_price: number } | null;
};

export type ProductData = {
  id: string;
  slug: string | null;
  name_ar: string;
  name_en: string;
  price: number;
  /**
   * Cheapest active sale-campaign price, when a campaign is running. `price`
   * always stays the regular price — renderers show it struck through.
   */
  sale_price?: number | null;
  /** Pre-sale price snapshot, shown struck through beside `sale_price`. */
  original_price?: number | null;
  description_ar: string | null;
  description_en: string | null;
  product_type: string;
  /** Minimum order quantity — a card that adds to cart must respect it. */
  min_order_qty: number | null;
  /** Increment the quantity stepper moves by — products sold in packs. */
  qty_step: number | null;
  track_stock: boolean;
  stock: number | null;
  option_names: { name_ar: string; name_en: string }[];
  product_images: ProductImage[];
  product_variants: ProductVariant[];
  category_name_ar?: string | null;
  category_name_en?: string | null;
  /** The unit `price` and `stock` are counted in. Null for a product without units. */
  base_unit?: UnitDTO | null;
  /** Units the customer may buy in. Empty for a product without units. */
  units?: ProductUnitDTO[];
  /** Product spec rows (products.specs). Cards keep only the chip picks. */
  specs?: ProductSpec[];
  /** The three below are set only for the quick view. */
  has_price_tiers?: boolean;
  price_tiers?: TierLike[];
  in_price_group?: boolean;
};

export type ProductSpec = {
  key_ar?: string;
  key_en?: string;
  value_ar?: string;
  value_en?: string;
};

/** Spec keys worth a chip on the glass-spec card (hardware buying criteria). */
const CHIP_SPEC_KEY =
  /Screen Size|Refresh Rate|Panel|GPU Memory|Socket|RAM Type|Capacity|Wattage|DPI/i;

/** "15 - 16", "15–16": two numbers joined by a dash. */
const RANGE_VALUE = /\d\s*["″”]?\s*[-–]\s*["″”]?\s*\d/;

const ARABIC_LETTER = /[\u0600-\u06FF]/;

/** The chip text: value_en, or value_ar only when it has no Arabic letters
 *  (chips are set in mono, which is never used for Arabic words). */
export function specChipValue(s: ProductSpec): string | null {
  if (s.value_en) return s.value_en;
  if (s.value_ar && !ARABIC_LETTER.test(s.value_ar)) return s.value_ar;
  return null;
}

/** Up to three chip-worthy specs, in the product's own order. */
export function pickSpecChips(
  specs: ProductSpec[] | null | undefined,
): ProductSpec[] {
  return (specs ?? [])
    .filter((s) => {
      const v = specChipValue(s);
      // A range ("15 - 16") is a filter bucket, not something the product owns.
      return CHIP_SPEC_KEY.test(s.key_en ?? "") && v && !RANGE_VALUE.test(v);
    })
    .slice(0, 3);
}

/** Minimal shape needed to decide whether a product is on sale. */
export type SaleFields = {
  price: number;
  sale_price?: number | null;
  original_price?: number | null;
};

/**
 * Resolve an active discount into the numbers renderers need, or null when
 * the product isn't discounted. Guards against campaigns whose snapshot is
 * missing or not actually cheaper than the original.
 */
export function resolveSale(
  p: SaleFields,
): { current: number; original: number; percentOff: number } | null {
  const current = p.sale_price;
  const original = p.original_price ?? p.price;
  if (current == null || current <= 0 || current >= original) return null;
  return {
    current,
    original,
    percentOff: Math.round(((original - current) / original) * 100),
  };
}

/**
 * Every prop handed to a client component is serialized into the page's RSC
 * payload, which streams ahead of the page HTML. The card never renders the
 * descriptions (rich-text JSON) or any image after the first; dropping them
 * took doprint.ps's homepage payload from 318 KB to 179 KB (41 → 27 KB gzip).
 */
export function toCardProduct(product: ProductData): ProductData {
  return {
    ...product,
    description_ar: null,
    description_en: null,
    product_images: product.product_images.slice(0, 1),
    specs: pickSpecChips(product.specs),
  };
}

/** Pre-resolved color swatch for display on the card */
export type ColorSwatch = {
  name: string;
  hex: string;
  imageUrl?: string | null;
};

/** A non-color option group with its unique values */
export type OptionGroup = {
  key: string;
  label_ar: string;
  label_en: string;
  values: string[];
};

/** Map of color value name → hex code, resolved from option_values table */
export type HexMap = Record<string, string>;

/** Fallback hex for common color names when option_values.color_hex is missing */
const COLOR_NAME_FALLBACKS: Record<string, string> = {
  // Arabic
  'أسود': '#000000', 'أبيض': '#ffffff', 'أحمر': '#ef4444', 'أزرق': '#3b82f6',
  'أخضر': '#22c55e', 'أصفر': '#eab308', 'برتقالي': '#f97316', 'وردي': '#ec4899',
  'بنفسجي': '#8b5cf6', 'رمادي': '#6b7280', 'بني': '#92400e', 'كحلي': '#1e3a5f',
  'بيج': '#d2b48c', 'ذهبي': '#d4af37', 'فضي': '#c0c0c0',
  // English
  'black': '#000000', 'white': '#ffffff', 'red': '#ef4444', 'blue': '#3b82f6',
  'green': '#22c55e', 'yellow': '#eab308', 'orange': '#f97316', 'pink': '#ec4899',
  'purple': '#8b5cf6', 'gray': '#6b7280', 'brown': '#92400e', 'navy': '#1e3a5f',
  'beige': '#d2b48c', 'gold': '#d4af37', 'silver': '#c0c0c0',
};

export function resolveColorHex(name: string, hexMap: HexMap): string {
  return hexMap[name] ?? COLOR_NAME_FALLBACKS[name] ?? COLOR_NAME_FALLBACKS[name.toLowerCase()] ?? '#d1d5db';
}

/**
 * Check if a value is a known color (exists in hexMap or fallback list).
 */
function isColorValue(value: string, hexMap: HexMap): boolean {
  return !!(hexMap[value] || COLOR_NAME_FALLBACKS[value] || COLOR_NAME_FALLBACKS[value.toLowerCase()]);
}

/**
 * Detect which option key is the "color" option by checking if any of its
 * values resolve to a hex code (via hexMap or fallback). Returns the key or null.
 */
function detectColorOptionKey(product: ProductData, hexMap: HexMap): string | null {
  // Collect unique values per option key
  const valuesByKey: Record<string, Set<string>> = {};
  for (const v of product.product_variants) {
    for (const [key, value] of Object.entries(v.options)) {
      if (!valuesByKey[key]) valuesByKey[key] = new Set();
      valuesByKey[key].add(value);
    }
  }

  // The color option is the one where at least one value has a hex code
  for (const [key, values] of Object.entries(valuesByKey)) {
    for (const val of values) {
      if (isColorValue(val, hexMap)) return key;
    }
  }

  return null;
}

/**
 * Extract all variant options split into color swatches + other option chips.
 * Auto-detects which option is "color" via hexMap — no hardcoded keys.
 */
export function extractVariantOptions(product: ProductData, hexMap: HexMap): {
  colors: ColorSwatch[];
  colorKey: string | null;
  otherOptions: OptionGroup[];
} {
  if (product.product_variants.length === 0) {
    return { colors: [], colorKey: null, otherOptions: [] };
  }

  const colorKey = detectColorOptionKey(product, hexMap);

  // Extract color swatches
  const colors: ColorSwatch[] = [];
  if (colorKey) {
    const seen = new Set<string>();
    for (const v of product.product_variants) {
      const colorName = v.options[colorKey];
      if (!colorName || seen.has(colorName)) continue;
      seen.add(colorName);
      colors.push({
        name: colorName,
        hex: resolveColorHex(colorName, hexMap),
        imageUrl: v.image_url ?? null,
      });
    }
  }

  // Extract all other options as chips
  const otherOptions: OptionGroup[] = [];
  // Use option_names order if available, otherwise collect from variant data
  const optionKeys = product.option_names.length > 0
    ? product.option_names
        .filter((o) => o.name_ar !== colorKey)
        .map((o) => ({ key: o.name_ar, label_ar: o.name_ar, label_en: o.name_en }))
    : Object.keys(
        product.product_variants.reduce<Record<string, true>>((acc, v) => {
          for (const k of Object.keys(v.options)) {
            if (k !== colorKey) acc[k] = true;
          }
          return acc;
        }, {})
      ).map((k) => ({ key: k, label_ar: k, label_en: k }));

  for (const opt of optionKeys) {
    const seen = new Set<string>();
    const values: string[] = [];
    for (const v of product.product_variants) {
      const val = v.options[opt.key];
      if (!val || seen.has(val)) continue;
      seen.add(val);
      values.push(val);
    }
    if (values.length > 0) {
      otherOptions.push({
        key: opt.key,
        label_ar: opt.label_ar,
        label_en: opt.label_en,
        values,
      });
    }
  }

  return { colors, colorKey, otherOptions };
}

/** Whether the product has any variant options */
export function hasVariants(product: ProductData): boolean {
  return product.product_variants.length > 0;
}
